import { api } from "@/modules/api";
import { getBackendApiErrorMessage } from "@/modules/api/errorHandling";
import type { ApiSchema } from "@/modules/api";

export type MemoryClaim = ApiSchema["CoreApi__MemoryClaimResponseSchema"];
export type MemoryEntity = ApiSchema["CoreApi__MemoryEntityListItemSchema"];
export type MemoryEntityDetail = ApiSchema["CoreApi__MemoryEntityDetailResponseSchema"];
export type MemoryEvidence = ApiSchema["CoreApi__MemoryEvidenceResponseSchema"];
export type MemoryManifest = ApiSchema["CoreApi__MemoryManifestResponseSchema"];
export type MemoryOverview = ApiSchema["CoreApi__MemoryOverviewResponseSchema"];
export type MemoryRun = ApiSchema["CoreApi__MemoryRunResponseSchema"];
export type MemoryType = ApiSchema["CoreApi__MemoryTypeResponseSchema"];
export type MemoryView = ApiSchema["CoreApi__MemoryViewResponseSchema"];
export type MemoryViewResults = ApiSchema["CoreApi__MemoryViewResultsResponseSchema"];
export type TrackMemoryResponse = ApiSchema["CoreApi__TrackMemoryResponseSchema"];

function unwrap<T>({ data, error }: { data?: T; error?: unknown }, fallbackMessage: string): T {
  if (error || data === undefined) {
    throw new Error(getBackendApiErrorMessage(error) ?? fallbackMessage);
  }
  return data;
}

export const getMemoryOverview = async (): Promise<MemoryOverview> => {
  const response = await api.get("/v2/memory", {});
  return unwrap(response, "Unable to load Memory");
};

export const trackCustomerIntelligence = async ({
  forceRebuild = false,
}: {
  forceRebuild?: boolean;
} = {}): Promise<TrackMemoryResponse> => {
  const response = await api.post("/v2/memory/customer-intelligence", {
    body: { force_rebuild: forceRebuild },
  });
  return unwrap(response, "Unable to start tracking customer intelligence");
};

export const backfillMemoryManifest = async (manifestId: string): Promise<TrackMemoryResponse> => {
  const response = await api.post("/v2/memory/manifests/{manifest_id}/backfill", {
    params: { path: { manifest_id: manifestId } },
  });
  return unwrap(response, "Unable to sync Memory");
};

export const updateMemoryManifestStatus = async ({
  manifestId,
  status,
}: {
  manifestId: string;
  status: MemoryManifest["status"];
}): Promise<MemoryManifest> => {
  const response = await api.patch("/v2/memory/manifests/{manifest_id}", {
    params: { path: { manifest_id: manifestId } },
    body: { status },
  });
  return unwrap(response, "Unable to update Memory tracking");
};

export const getMemoryView = async ({
  viewId,
  query,
  limit = 50,
  offset = 0,
}: {
  viewId: string;
  query?: string;
  limit?: number;
  offset?: number;
}): Promise<MemoryViewResults> => {
  const response = await api.get("/v2/memory/views/{view_id}", {
    params: {
      path: { view_id: viewId },
      query: { query: query || undefined, limit, offset },
    },
  });
  return unwrap(response, "Unable to load this Memory view");
};

export const getMemoryEntity = async (entityId: string): Promise<MemoryEntityDetail> => {
  const response = await api.get("/v2/memory/entities/{entity_id}", {
    params: { path: { entity_id: entityId } },
  });
  return unwrap(response, "Unable to load this Memory item");
};

export const updateMemoryEntity = async ({
  entityId,
  displayName,
  summary,
}: {
  entityId: string;
  displayName: string;
  summary: string;
}): Promise<MemoryEntityDetail> => {
  const response = await api.patch("/v2/memory/entities/{entity_id}", {
    params: { path: { entity_id: entityId } },
    body: { display_name: displayName, summary },
  });
  return unwrap(response, "Unable to update this Memory item");
};

export const correctMemoryClaim = async ({
  entityId,
  propertyKey,
  displayValue,
}: {
  entityId: string;
  propertyKey: string;
  displayValue: string;
}): Promise<MemoryEntityDetail> => {
  const response = await api.post("/v2/memory/entities/{entity_id}/claims", {
    params: { path: { entity_id: entityId } },
    body: {
      property_key: propertyKey,
      value: displayValue,
      display_value: displayValue,
    },
  });
  return unwrap(response, "Unable to save this correction");
};
